const groupPrefix = createApiRoute(ApiModule.PRODUCTION_PLANNING, ApiModule.EMPLOYEE, ApiDataModel.SIMULATIONS)

export const apiProductionSimulations = {
	fetchSimulation: async (simulationID: number): Promise<IApiResponseModel<IProductionSimulationModel>> => {
		const { data } = await apiCommon<IProductionSimulation>(`${groupPrefix}/${simulationID}`, {
			method: 'GET',
		})

		return { data: ProductionSimulation(data) }
	},

	fetchSimulations: async (
		productionPlanID: number,
		page: number = 1,
		perPage: number = 10,
	): Promise<IApiResponseModel<IProductionSimulationModel[]>> => {
		const { data, meta } = await apiCommon<IProductionSimulation[]>(groupPrefix, {
			method: 'GET',
			...createQuery(
				{ page, perPage },
				{ production_plan_id: productionPlanID ? productionPlanID : undefined },
				undefined,
				undefined,
			),
		})

		return { data: data ? data.map(d => ProductionSimulation(d)) : [], meta }
	},

	fetchSimulationMeals: async (
		simulationID: number,
	): Promise<IApiResponseModel<IProductionSimulationMealModel[]>> => {
		const { data } = await apiCommon<IProductionSimulationMeal[]>(`${groupPrefix}/${simulationID}/meals`, {
			method: 'GET',
		})

		return { data: data ? data.map(d => ProductionSimulationMeal(d)) : [] }
	},

	updateSimulationMeals: async (
		simulationID: number,
		meals: IProductionSimulationMealRequest[],
	): Promise<IApiResponseModel<IProductionSimulationModel>> => {
		const { data } = await apiCommon<IProductionSimulation>(`${groupPrefix}/${simulationID}/meals`, {
			method: 'PUT',
			body: { meals },
		})

		return { data: ProductionSimulation(data) }
	},

	fetchSimulationProducts: async (
		simulationID: number,
	): Promise<IApiResponseModel<IProductionSimulationProductModel[]>> => {
		const { data } = await apiCommon<IProductionSimulationProduct[]>(`${groupPrefix}/${simulationID}/products`, {
			method: 'GET',
		})

		return { data: data ? data.map(d => ProductionSimulationProduct(d)) : [] }
	},

	updateSimulationProducts: async (
		simulationID: number,
		products: IProductionSimulationProductRequest[],
	): Promise<IApiResponseModel<IProductionSimulationModel>> => {
		const { data } = await apiCommon<IProductionSimulation>(`${groupPrefix}/${simulationID}/products`, {
			method: 'PUT',
			body: { products },
		})

		return { data: ProductionSimulation(data) }
	},

	fetchSimulationPositions: async (
		simulationID: number,
	): Promise<IApiResponseModel<IProductionSimulationPositionModel[]>> => {
		const { data } = await apiCommon<IProductionSimulationPosition[]>(`${groupPrefix}/${simulationID}/positions`, {
			method: 'GET',
		})

		return { data: data ? data.map(d => ProductionSimulationPosition(d)) : [] }
	},

	updateSimulationPositions: async (
		simulationID: number,
		productionStages: IProductionSimulationPositionRequest[],
	): Promise<IApiResponseModel<IProductionSimulationModel>> => {
		const { data } = await apiCommon<IProductionSimulation>(`${groupPrefix}/${simulationID}/positions`, {
			method: 'PUT',
			body: { production_simulation_stages: productionStages },
		})

		return { data: ProductionSimulation(data) }
	},

	fetchSimulationSchedule: async (
		simulationID: number,
	): Promise<IApiResponseModel<IProductionSimulationScheduleResponse>> => {
		const { data } = await apiCommon<IProductionSimulationScheduleResponse>(
			`${groupPrefix}/${simulationID}/schedule`,
			{
				method: 'POST',
			},
		)

		return { data }
	},

	fetchSimulationPackaging: async (
		simulationID: number,
	): Promise<IApiResponseModel<IProductionSimulationThroughputModel[]>> => {
		const { data } = await apiCommon<IProductionSimulationThroughput[]>(
			`${groupPrefix}/${simulationID}/packaging-throughput`,
			{
				method: 'GET',
			},
		)

		return { data: data ? data.map(d => ProductionSimulationThroughput(d)) : [] }
	},

	fetchSimulationInstructions: async (simulationID: number) => {
		const { data } = await apiCommon<IProductionSimulationInstruction[]>(
			`${groupPrefix}/${simulationID}/instructions`,
			{
				method: 'GET',
			},
		)

		return { data: data ? data.map(d => ProductionSimulationInstruction(d)) : [] }
	},

	fetchSimulationMealInstructions: async (simulationID: number) => {
		const { data } = await apiCommon<IProductionPlanDetails>(`${groupPrefix}/${simulationID}/instructions/meals`, {
			method: 'GET',
		})

		return { data: ProductionPlanDetails(data) }
	},

	fetchSimulationInstructionsPackaging: async (simulationID: number) => {
		const { data } = await apiCommon<IProductionSimulationInstruction[]>(
			`${groupPrefix}/${simulationID}/instructions/packing`,
			{
				method: 'GET',
			},
		)

		return { data: data ? data.map(d => ProductionSimulationInstruction(d)) : [] }
	},

	storeSimulationPackaging: async (simulationID: number): Promise<{ message: string }> => {
		const { data } = await apiCommon<{ message: string }>(`${groupPrefix}/${simulationID}/packaging-throughput`, {
			method: 'POST',
		})

		return data
	},

	downloadSimulationInstructions: async (simulationID: number, dispatchID: number) =>
		await apiCommon<Blob>(`${groupPrefix}/${simulationID}/instructions/download/${dispatchID}`, {
			method: 'GET',
			isFileDownload: true,
		}),

	generateSimulationInstructions: async (
		simulationID: number,
		positionsByLanguage: Record<string, number[]>,
		context: EProductionPlansInstructionType,
	) => {
		const { data } = await apiCommon<IProductionSimulationInstructionGenerateResponse>(
			`${groupPrefix}/${simulationID}/instructions/generate`,
			{
				method: 'POST',
				body: { positions_by_language: positionsByLanguage, instructions_type: context },
			},
		)

		return { data }
	},

	generateSimulationMealInstructions: async (simulationID: number, mealsByLanguage: Record<string, number[]>) => {
		const { data } = await apiCommon<IProductionSimulationInstructionGenerateResponse>(
			`${groupPrefix}/${simulationID}/instructions/generate-meals`,
			{
				method: 'POST',
				body: { meals_by_language: mealsByLanguage, instructions_type: 'MealInstruction' },
			},
		)

		return { data }
	},

	generateSimulationMealShortInstructions: async (
		simulationID: number,
		mealsByLanguage: Record<string, number[]>,
	) => {
		const { data } = await apiCommon<IProductionSimulationInstructionGenerateResponse>(
			`${groupPrefix}/${simulationID}/instructions/generate-meals`,
			{
				method: 'POST',
				body: { meals_by_language: mealsByLanguage, instructions_type: 'MealShortInstruction' },
			},
		)

		return { data }
	},

	generateSimulationLogistics: async (simulationID: number, transportsByLanguage: Record<string, number[]>) => {
		const { data } = await apiCommon<IProductionSimulationInstructionGenerateResponse>(
			`${groupPrefix}/${simulationID}/logistic/generate`,
			{
				method: 'POST',
				body: { tasks_by_language: transportsByLanguage, instructions_type: 'LogisticStageInstruction' },
			},
		)

		return { data }
	},

	fetchSimulationInstructionsGenerated: async (simulationID: number, context: EProductionPlansInstructionType) => {
		const { data } = await apiCommon<IProductionSimulationInstructionGenerateListResponse[]>(
			`${groupPrefix}/${simulationID}/instructions/generated`,
			{
				method: 'GET',
				query: {
					context,
				},
			},
		)

		return { data: data ? data.map(d => ProductionSimulationInstructionGenerateList(d)) : [] }
	},

	updateSimulationPackaging: async (
		simulationID: number,
		packagingID: number,
		requestData: IProductionSimulationThroughputRequest,
		options?: { signal?: AbortSignal },
	): Promise<IApiResponseModel<IProductionSimulationThroughputItemModel>> => {
		const { data } = await apiCommon<IProductionSimulationThroughputItem>(
			`${groupPrefix}/${simulationID}/packaging-throughput/${packagingID}`,
			{
				method: 'PUT',
				body: requestData,
				signal: options?.signal,
			},
		)

		return { data: ProductionSimulationThroughputItem(data) }
	},

	updateSimulationFinal: async (simulationID: number): Promise<{ data: any }> => {
		await apiCommon(`${groupPrefix}/${simulationID}/final`, {
			method: 'POST',
		})

		return { data: null }
	},

	duplicateSimulation: async (simulationID: number): Promise<IApiResponseModel<{ id: number }>> => {
		const { data } = await apiCommon<IProductionSimulation>(`${groupPrefix}/${simulationID}/duplicate`, {
			method: 'POST',
		})

		return { data: { id: data.id } }
	},

	overwriteSimulation: async (simulationID: number): Promise<void> => {
		await apiCommon<IProductionSimulation>(`${groupPrefix}/${simulationID}/overwrite`, {
			method: 'POST',
		})
	},

	autoFillSimulation: async (simulationID: number): Promise<void> => {
		await apiCommon<IProductionSimulation>(`${groupPrefix}/${simulationID}/auto-fill`, {
			method: 'POST',
		})
	},

	insertActivityToSchedule: async (
		simulationID: number,
		data: IProductionActivityRequestModel,
	): Promise<any[] | null> => {
		const response = await apiCommon<any>(`${groupPrefix}/${simulationID}/machines/activities`, {
			method: 'POST',
			body: ProductionActivityRequestToJson(data),
		})

		if (!response || response.status === EHttpStatusCodes.HTTP_STATUS_NO_CONTENT) {
			return null
		}

		return response.data
	},

	fetchSimulationPackagingSchedule: async (simulationID: number): Promise<IApiResponseModel<any>> => {
		const { data } = await apiCommon<any>(`${groupPrefix}/${simulationID}/packaging/schedule`, {
			method: 'POST',
		})

		return { data }
	},

	downloadSimulationWarehouse: async (simulationID: number) =>
		await apiCommon<Blob>(`${groupPrefix}/${simulationID}/materials-stock/summary-file`, {
			method: 'GET',
			isFileDownload: true,
		}),

	downloadSimulationSemiProductsChecklist: async (simulationID: number) =>
		await apiCommon<Blob>(`${groupPrefix}/${simulationID}/materials-stock/semi-products-checklist-file`, {
			method: 'GET',
			isFileDownload: true,
		}),

	fetchSimulationLogistics: async (simulationID: number) => {
		const { data } = await apiCommon<IProductionSimulationLogistic[]>(`${groupPrefix}/${simulationID}/logistic`, {
			method: 'GET',
		})

		return { data: data ? data.map(d => ProductionSimulationLogistic(d)) : [] }
	},

	fetchSimulationWarehouse: async (simulationID: number) => {
		const { data } = await apiCommon<IProductionSimulationWarehouse>(
			`${groupPrefix}/${simulationID}/materials-stock/summary`,
			{
				method: 'GET',
			},
		)

		return { data: ProductionSimulationWarehouse(data) }
	},

	fetchSimulationReplenishments: async (
		simulationID: number,
	): Promise<IApiResponseModel<IProductionSimulationReplenishmentModel[]>> => {
		const { data } = await apiCommon<IProductionSimulationReplenishment[]>(
			`${groupPrefix}/${simulationID}/replenishments`,
			{
				method: 'GET',
			},
		)

		return { data: data ? data.map(d => ProductionSimulationReplenishment(d)) : [] }
	},

	insertReplenishmentToSchedule: async (
		simulationID: number,
		payload: IProductionSimulationReplenishmentRequestModel,
	): Promise<{ data: any }> => {
		const data = await apiCommon<any>(`${groupPrefix}/${simulationID}/replenishments`, {
			method: 'POST',
			body: ProductionSimulationReplenishmentRequestToJson(payload),
		})

		return { data }
	},

	fetchSimulationRecipesContainingMaterial: async (
		productionSimulationID: number,
		search: string,
		searchBy: EDietSearchComponentsFilter,
	): Promise<IApiResponseModel<IProductionSimulationMaterialRecipesResponseModel>> => {
		const { data } = await apiCommon<IProductionSimulationMaterialRecipesResponse>(
			`${groupPrefix}/${productionSimulationID}/materials-stock/recipes?search=${search}&search_by=${searchBy}`,
			{
				method: 'GET',
			},
		)

		return {
			data: ProductionSimulationMaterialRecipesResponse(data),
		}
	},
}
